import type { Auth } from 'firebase/auth';
import { type Firestore, doc, setDoc } from 'firebase/firestore';
import { type Functions, httpsCallable } from 'firebase/functions';

import type { RegistrationRequestDraft } from '../../core/identity/accountLink';
import {
  AccountLinkError,
  type AccountLinkPort,
  type IdentityLookup,
  type LinkFailureCode,
} from '../../features/auth/application/ports/accountLinkPort';

/**
 * Calls the trusted account-linking functions (blueprint §11.1).
 *
 * Raw platform errors are translated here and never escape (§4.5). The screens
 * consume `AccountLinkError` and know nothing about Firebase.
 */

/** The refusal codes the functions attach to their errors. */
const CODE_MAP: Record<string, { code: LinkFailureCode; message: string }> = {
  'wrong-domain': {
    code: 'wrong_domain',
    message: 'الدخول مقتصر على حسابات المدرسة الرسمية.',
  },
  'already-linked': {
    code: 'already_linked',
    message: 'هذا الحساب مرتبط بسجل موظف بالفعل.',
  },
  'record-taken': {
    code: 'record_taken',
    message: 'هذا السجل مرتبط بحساب آخر. راجع الموارد البشرية.',
  },
  'candidate-expired': {
    code: 'candidate_expired',
    message: 'انتهت صلاحية طلب التأكيد. أعد المحاولة.',
  },
  'ambiguous-record': {
    code: 'unknown',
    message: 'وُجد أكثر من سجل بهذا البريد. راجع الموارد البشرية.',
  },
};

function translate(error: unknown): AccountLinkError {
  const details =
    typeof error === 'object' && error !== null && 'details' in error
      ? (error.details as { code?: unknown } | null)
      : null;

  const known = typeof details?.code === 'string' ? CODE_MAP[details.code] : undefined;
  if (known) return new AccountLinkError(known.code, known.message);

  const platformCode =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

  if (platformCode.includes('unavailable') || platformCode.includes('deadline')) {
    return new AccountLinkError('network', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }

  // §14.1 — a safe message. The platform code says more about our stack than
  // about the employee's problem.
  return new AccountLinkError('unknown', 'تعذّر إتمام العملية. حاول مرة أخرى.');
}

export function createFirebaseAccountLinkAdapter(
  functions: Functions,
  firestore: Firestore,
  auth: Auth,
): AccountLinkPort {
  const lookup = httpsCallable<void, IdentityLookup>(functions, 'lookupIdentity');
  const confirm = httpsCallable<{ candidateToken: string }, { employeeId: string }>(
    functions,
    'confirmMyIdentity',
  );
  const decline = httpsCallable<{ candidateToken: string }, unknown>(
    functions,
    'declineMyIdentity',
  );

  return {
    async lookupMyIdentity() {
      try {
        const result = await lookup();
        return result.data;
      } catch (error) {
        throw translate(error);
      }
    },

    async confirmIdentity(candidateToken) {
      try {
        await confirm({ candidateToken });
      } catch (error) {
        throw translate(error);
      }
    },

    async declineIdentity(candidateToken) {
      try {
        await decline({ candidateToken });
      } catch (error) {
        throw translate(error);
      }
    },

    async submitRegistrationRequest(draft: RegistrationRequestDraft) {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new AccountLinkError('wrong_domain', 'يجب تسجيل الدخول بحساب المدرسة.');
      }

      try {
        // uid and email come from the signed-in user, not from the form, and
        // the rules verify both against the token anyway. Status is fixed at
        // 'pending' here and by the rules — nobody approves themselves by
        // writing a field.
        await setDoc(doc(firestore, 'registrationRequests', user.uid), {
          schemaVersion: 1,
          authUid: user.uid,
          email: user.email.toLowerCase(),
          reason: draft.reason,
          fullName: draft.fullName,
          phone: draft.phone,
          branchId: draft.branchId,
          ...(draft.mismatchedEmployeeId === undefined
            ? {}
            : { mismatchedEmployeeId: draft.mismatchedEmployeeId }),
          status: 'pending',
          submittedAt: new Date().toISOString(),
        });
      } catch (error) {
        throw translate(error);
      }
    },
  };
}
